import { makeAutoObservable, runInAction } from 'mobx';
import { createAxiosEntrepriseInstance } from '../axiosEntreprise';

/**
 * WorkspaceStore — Phase 2 SEIZO
 *
 * Gère la liste des workspaces de l'organisation et le workspace actif.
 * Le workspace actif est persisté dans localStorage pour survivre aux rechargements.
 * Phase 2 : le paramètre `ent` est optionnel (URL sans code espace).
 * L'authentification se fait via le token current_apiToken (cookie).
 */
class WorkspaceStore {
    workspaces = [];
    activeWorkspace = null; // { id, name, slug, totems_count, ... }
    loading = false;
    error = null;

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * Charge la liste des workspaces depuis l'API.
     * Restaure le workspace actif depuis localStorage si disponible.
     * Le paramètre `ent` est optionnel (Phase 2 : URL sans code espace).
     */
    async loadWorkspaces(ent) {
        this.loading = true;
        this.error = null;
        // Clé localStorage : utiliser 'current' si ent est undefined (Phase 2)
        const storageKey = ent ? `${ent}_activeWorkspaceId` : 'current_activeWorkspaceId';
        try {
            const axios = createAxiosEntrepriseInstance(ent);
            const res = await axios.get('/api/workspaces');
            runInAction(() => {
                this.workspaces = res.data.data || [];
                this.loading = false;

                // Restaurer le workspace actif depuis localStorage
                const savedId = localStorage.getItem(storageKey);
                if (savedId) {
                    const found = this.workspaces.find(w => String(w.id) === String(savedId));
                    this.activeWorkspace = found || this.workspaces[0] || null;
                } else {
                    // Par défaut : premier workspace (Par défaut)
                    this.activeWorkspace = this.workspaces[0] || null;
                }
            });
        } catch (e) {
            runInAction(() => {
                this.error = 'Impossible de charger les workspaces.';
                this.loading = false;
            });
        }
    }

    /**
     * Change le workspace actif et le persiste dans localStorage.
     */
    setActiveWorkspace(ent, workspace) {
        this.activeWorkspace = workspace;
        const storageKey = ent ? `${ent}_activeWorkspaceId` : 'current_activeWorkspaceId';
        if (workspace) {
            localStorage.setItem(storageKey, String(workspace.id));
        } else {
            localStorage.removeItem(storageKey);
        }
    }

    /**
     * Réinitialise le store (ex: lors d'un changement d'espace).
     */
    reset() {
        this.workspaces = [];
        this.activeWorkspace = null;
        this.loading = false;
        this.error = null;
    }
}

export default WorkspaceStore;
